<template>
  <div class="musubi-page musubi-page--lead">
    <header class="musubi-page__head lead-head">
      <div>
        <a href="/acquire/prospects" class="lead-back"><CygnusIcon name="arrow-left" /> Prospects</a>
        <h1 v-if="lead">{{ lead.name || lead.company || lead.host || ('Prospect #' + lead.id) }}
          <MusubiStatusPill v-if="lead.is_lead" status="lead" tone="success" label="Lead" class="align-middle" title="Shows intent — buying signal or engagement" />
          <MusubiStatusPill v-else status="prospect" tone="neutral" label="Prospect" class="align-middle" title="Discovered, no intent signal yet" />
          <MusubiStatusPill v-if="lead.unsubscribed" status="unsubscribed" tone="danger" label="Unsubscribed" class="align-middle" title="Unsubscribed — cannot be added to a drip flow" />
          <span class="lead-temp">{{ lead.temperature === 'cold' ? '❄️ cold' : '🔥 warm' }}</span>
        </h1>
        <h1 v-else>Prospect</h1>
      </div>
      <MusubiStatusPill v-if="lead" :status="lead.stage || 'new'" />
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>
    <div v-if="loading" class="text-muted small py-3">Loading…</div>

    <template v-if="lead">
      <!-- Action bar -->
      <section class="actions-bar">
        <button v-if="!terminal && lead.route === 'drip'" class="btn btn-success btn-sm" :disabled="busy" @click="promote"><CygnusIcon name="external-link" class="me-1" />Promote to CRM</button>
        <button v-if="!terminal" class="btn btn-outline-primary btn-sm" :disabled="busy" @click="enrich">
          <CygnusIcon :name="busy ? 'spinner' : 'bolt'" :spin="busy" class="me-1" />{{ busy ? 'Enriching…' : 'Enrich all' }}
        </button>
        <button v-if="!terminal" class="btn btn-outline-info btn-sm" :disabled="busyAi || busy" @click="aiEnrich" title="Force an AI pass (company / address / outreach / signals) — runs even if AI methods are off">
          <CygnusIcon :name="busyAi ? 'spinner' : 'bolt'" :spin="busyAi" class="me-1" />{{ busyAi ? 'AI pass…' : 'AI enrich' }}
        </button>
        <button v-if="canEditProspect" class="btn btn-outline-secondary btn-sm" @click="openEdit"><CygnusIcon name="pen" class="me-1" />Edit details</button>
        <button v-if="!lead.unsubscribed" class="btn btn-outline-primary btn-sm" @click="openCompose()"><CygnusIcon name="envelope" class="me-1" />Send email</button>
        <button v-if="!terminal" class="btn btn-outline-success btn-sm" :disabled="busy" @click="act('qualify','Qualified')">Qualify</button>
        <button v-if="!terminal" class="btn btn-outline-danger btn-sm" :disabled="busy" @click="act('reject','Rejected')">Reject</button>
      </section>

      <!-- Manual edit panel — set what the crawl/CH missed; saving re-enriches. -->
      <MusubiSectionCard v-if="editing && canEditProspect" title="Edit details" icon="pen" class="lead-edit">
        <p class="text-muted small">Add anything the crawl or Companies House missed. <strong>Saving re-enriches</strong> — a company number does a direct CH lookup; a website kicks off a crawl.</p>
        <div class="lead-edit-grid">
          <label>Website<input v-model="edit.website" class="form-control form-control-sm" placeholder="https://…"></label>
          <label>Company name<input v-model="edit.company" class="form-control form-control-sm"></label>
          <label>Company number<input v-model="edit.company_number" class="form-control form-control-sm" placeholder="e.g. 06429495"></label>
          <label>Registered address<input v-model="edit.address" class="form-control form-control-sm"></label>
          <label>Region (postcode area)<input v-model="edit.region" class="form-control form-control-sm" placeholder="e.g. HX"></label>
          <label>Email<input v-model="edit.email" class="form-control form-control-sm"></label>
          <label>Phone<input v-model="edit.phone" class="form-control form-control-sm"></label>
          <label>Current agency<input v-model="edit.current_agency" class="form-control form-control-sm"></label>
        </div>
        <div class="lead-edit-actions">
          <button class="btn btn-primary btn-sm" :disabled="savingEdit" @click="saveEdit">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="savingEdit" />Save &amp; re-enrich
          </button>
          <button class="btn btn-link btn-sm" @click="editing = false">Cancel</button>
        </div>
      </MusubiSectionCard>

      <div class="lead-kpis">
        <MusubiKpiCard label="Intent score" :value="lead.score" icon="flame" />
        <MusubiKpiCard label="Fit score" :value="lead.fit_score" icon="pin" />
        <MusubiKpiCard label="Engagement" :value="lead.engagement_score" :delta="lead.route === 'drip' ? 'in drip' : ''" icon="envelope-open-text" />
        <MusubiKpiCard label="Enrichment" :value="lead.enrichment_status || '—'" :delta="lead.enrichment_provider || ''" icon="bolt" />
        <MusubiKpiCard label="Route" :value="lead.route || 'none'" icon="diagram" />
      </div>

      <MusubiSectionCard v-if="lead.pain_points && lead.pain_points.length" title="Outreach hooks" icon="pin" class="lead-hooks">
        <p class="text-muted small mb-2">Concrete pain points to lead with — derived from enrichment.</p>
        <MusubiStatusPill v-for="p in lead.pain_points" :key="p.key" :status="p.key" :label="p.label" class="me-2 mb-1"
          :tone="p.severity === 'high' ? 'danger' : (p.severity === 'med' ? 'warning' : 'neutral')" />
      </MusubiSectionCard>

      <div class="lead-grid">
        <MusubiSectionCard title="Firmographics" icon="building">
          <dl class="lead-dl">
            <div><dt>Company</dt><dd>{{ lead.company || '—' }}</dd></div>
            <div><dt>Website</dt><dd><a v-if="lead.website || lead.profile_url" :href="lead.website || lead.profile_url" target="_blank" rel="noopener">{{ lead.website || lead.profile_url }}</a><span v-else>—</span></dd></div>
            <div><dt>Framework</dt><dd>
              {{ lead.website_framework || '—' }}
              <span v-if="lead.website_version" class="text-muted">{{ lead.website_version }}</span>
              <MusubiStatusPill v-if="lead.website_eol === 'eol'" status="eol" tone="danger" label="EOL" class="ms-1" />
              <MusubiStatusPill v-else-if="lead.website_eol === 'outdated'" status="outdated" tone="warning" label="outdated" class="ms-1" />
              <MusubiStatusPill v-else-if="lead.website_eol === 'current'" status="current" tone="success" label="current" class="ms-1" />
            </dd></div>
            <div v-if="lead.domain_age_years != null || lead.content_freshness"><dt>Age / freshness</dt><dd>
              <span v-if="lead.domain_age_years != null">{{ lead.domain_age_years }} yr{{ lead.domain_age_years === 1 ? '' : 's' }} old</span>
              <MusubiStatusPill v-if="lead.content_freshness === 'stale'" status="stale" tone="danger" label="stale" class="ms-1" />
              <MusubiStatusPill v-else-if="lead.content_freshness === 'aging'" status="aging" tone="warning" label="aging" class="ms-1" />
              <MusubiStatusPill v-else-if="lead.content_freshness === 'fresh'" status="fresh" tone="success" label="fresh" class="ms-1" />
              <span v-if="lead.last_updated" class="text-muted ms-1">updated {{ lead.last_updated }}</span>
            </dd></div>
            <div><dt>Tech stack</dt><dd>{{ lead.tech_stack || '—' }}</dd></div>
            <div><dt>Current agency</dt><dd>
              <template v-if="lead.current_agency || lead.current_agency_url">
                <!-- Primary link → the agency's OWN record inside Musubi -->
                <a v-if="lead.current_agency_lead_id" :href="`/acquire/prospects/${lead.current_agency_lead_id}`">{{ lead.current_agency || lead.current_agency_url }}</a>
                <a v-else-if="lead.current_agency_url" :href="lead.current_agency_url" target="_blank" rel="noopener">{{ lead.current_agency || lead.current_agency_url }}</a>
                <span v-else>{{ lead.current_agency }}</span>
                <!-- Secondary link → the agency's external website -->
                <a v-if="lead.current_agency_url" :href="lead.current_agency_url" target="_blank" rel="noopener" class="ms-2 small text-muted" title="Agency website">site&nbsp;↗</a>
                <MusubiStatusPill v-if="lead.agency_status === 'dissolved'" status="dissolved" tone="danger" label="dissolved" class="ms-2" title="Agency company dissolved" />
                <MusubiStatusPill v-else-if="lead.agency_status === 'dead'" status="dead" tone="warning" label="site gone" class="ms-2" title="Agency site no longer live" />
              </template>
              <span v-else>—</span>
            </dd></div>
            <div><dt>Address</dt><dd>{{ lead.address || '—' }}</dd></div>
            <div><dt>Operating area</dt><dd>{{ lead.operating_area || '—' }}</dd></div>
            <div><dt>Region</dt><dd>{{ lead.region || '—' }}</dd></div>
            <div><dt>Email</dt><dd>
              <span v-if="lead.email">{{ lead.email }}</span><span v-else>—</span>
              <MusubiStatusPill v-if="lead.email && emailVerify" :status="emailVerify.status" :tone="emailVerify.tone" :label="emailVerify.label" class="ms-1" :title="emailVerify.title" />
            </dd></div>
            <div><dt>Phone</dt><dd>
              <span v-if="lead.phones && lead.phones.length">{{ lead.phones.join(' · ') }}</span>
              <span v-else>{{ lead.phone || '—' }}</span>
              <MusubiStatusPill v-if="tpsVerdict" :status="tpsVerdict.status" :tone="tpsVerdict.tone" :label="tpsVerdict.label" class="ms-1" :title="tpsVerdict.title" />
            </dd></div>
            <div v-if="lead.opening_hours"><dt>Opening hours</dt><dd>{{ lead.opening_hours }}</dd></div>
            <div v-if="hasSocial"><dt>Social</dt><dd class="lead-social">
              <a v-for="(url, platform) in lead.social" :key="platform" :href="url" target="_blank" rel="noopener" :title="url">{{ platform }}</a>
            </dd></div>
          </dl>
        </MusubiSectionCard>

        <MusubiSectionCard title="Registration (Companies House)" icon="building">
          <div v-if="lead.registration_status === 'unregistered'" class="alert alert-warning py-2 small mb-2">
            <CygnusIcon name="warning" class="me-1" /><strong>Not found in Companies House.</strong>
            Likely a sole trader / unincorporated — treat as an individual (GDPR-protected); avoid cold B2B outreach without a lawful basis.
          </div>
          <dl class="lead-dl">
            <div><dt>Status</dt><dd>
              <span v-if="lead.registration_status === 'registered'" class="text-success">Registered</span>
              <span v-else-if="lead.registration_status === 'unregistered'" class="text-danger">Unregistered (sole trader?)</span>
              <span v-else-if="lead.registration_status === 'public_sector'" class="text-info">Public sector (council/NHS/school)</span>
              <span v-else class="text-muted">— not checked</span>
            </dd></div>
            <div><dt>Company no.</dt><dd>
              <a v-if="lead.company_number" :href="`https://find-and-update.company-information.service.gov.uk/company/${lead.company_number}`" target="_blank" rel="noopener" :title="`View ${lead.company_number} on Companies House`">{{ lead.company_number }} <CygnusIcon name="external-link" /></a>
              <span v-else>—</span>
              <MusubiStatusPill v-if="lead.website_disclosure_missing" status="disclosure_missing" tone="warning" label="No company number on website (legally required disclosure)" class="ms-1" title="A UK limited company must display its registered number on its website (Companies Act 2006 trading disclosures). None was found on this site." />
            </dd></div>
            <div v-if="lead.match_confidence != null"><dt>Match confidence</dt><dd>
              <MusubiStatusPill status="confidence" :tone="confidenceTone" :label="lead.match_confidence + '/100'" />
              <span v-if="lead.match_confidence < 50" class="text-danger small ms-1">low — review the match (name/region/distance)</span>
            </dd></div>
            <div><dt>CH status</dt><dd>{{ lead.company_status || '—' }}</dd></div>
            <div><dt>Size / employees</dt><dd>
              <MusubiStatusPill v-if="lead.company_size" status="size" tone="neutral" :label="lead.company_size" class="me-1" />
              <span v-if="lead.employee_count">~{{ lead.employee_count }} staff <span class="text-muted small">({{ lead.employee_count_source }})</span></span>
              <span v-if="!lead.company_size && !lead.employee_count" class="text-muted">—</span>
            </dd></div>
            <div v-if="lead.hiring || lead.careers_url"><dt>Hiring</dt><dd>
              <template v-if="lead.hiring">
                <CygnusIcon name="user-plus" class="me-1 text-success" />
                <MusubiStatusPill status="hiring" tone="success" label="Currently hiring" class="me-1" title="Open vacancies detected on the company's careers page — a growth + budget signal." />
                <span v-if="lead.hiring_detected_at" class="text-muted small me-1" :title="fmtDate(lead.hiring_detected_at)">since {{ relTime(lead.hiring_detected_at) }}</span>
                <span v-if="lead.hiring_roles && lead.hiring_roles.length" class="lead-hiring-roles">
                  <MusubiStatusPill v-for="(r, i) in lead.hiring_roles" :key="i" :status="r.category" tone="neutral" :label="r.role_title" class="me-1 mb-1" :title="`Category: ${r.category}`" />
                </span>
                <span v-else class="text-muted small">roles not itemised</span>
              </template>
              <span v-else class="text-muted">Careers page found — no open vacancies right now</span>
              <a v-if="lead.careers_url" :href="lead.careers_url" target="_blank" rel="noopener" class="small ms-1" title="Open the careers page">careers page <CygnusIcon name="external-link" /></a>
            </dd></div>
            <div v-if="lead.budget_band"><dt>Likely budget</dt><dd>
              <MusubiStatusPill status="budget" :tone="budgetTone" :label="lead.budget_band" />
              <span v-if="financialsLabel" class="text-muted small ms-1">{{ financialsLabel }}</span>
            </dd></div>
            <div><dt>Decision-makers</dt><dd>
              <div v-if="lead.contacts && lead.contacts.length" class="lead-contacts">
                <div v-for="(c, i) in lead.contacts" :key="i" class="lead-contact">
                  <span class="lead-contact__name">{{ c.name }}</span>
                  <MusubiStatusPill v-if="c.primary" status="primary" tone="accent" label="primary" class="ms-1" title="Most likely point of contact" />
                  <span class="text-muted small ms-1" v-if="c.occupation || c.role">· {{ c.occupation || c.role }}</span>
                  <span class="text-muted small ms-1" v-if="c.appointed">· since {{ c.appointed.slice(0,4) }}</span>
                  <div class="small">
                    <span v-if="c.email">
                      <a :href="`mailto:${c.email}`">{{ c.email }}</a>
                      <MusubiStatusPill v-if="c.email_status === 'found'" status="found" tone="success" label="found" class="ms-1" title="Published on the company's own website" />
                      <MusubiStatusPill v-else-if="c.email_status === 'verified'" status="verified" tone="success" label="verified" class="ms-1" title="Mailbox verified" />
                      <MusubiStatusPill v-else-if="c.email_status === 'inferred'" status="inferred" tone="warning" :label="'inferred' + (c.email_confidence != null ? ` ${c.email_confidence}%` : '')" class="ms-1" :title="`Inferred from the firm's email pattern (confidence ${c.email_confidence ?? '?'}%)`" />
                    </span>
                    <span v-else class="text-muted">no email yet</span>
                  </div>
                </div>
              </div>
              <span v-else-if="lead.directors && lead.directors.length">
                <span v-for="(d, i) in lead.directors" :key="i" class="lead-director">{{ d.name }}</span>
              </span>
              <span v-else class="text-muted">—</span>
            </dd></div>
            <div v-if="lead.employees && lead.employees.length"><dt>Team / employees</dt><dd>
              <div class="lead-contacts">
                <div v-for="(e, i) in lead.employees" :key="i" class="lead-contact">
                  <span class="lead-contact__name">{{ e.name }}</span>
                  <span class="text-muted small ms-1" v-if="e.title">· {{ e.title }}</span>
                  <div class="small">
                    <span v-if="e.email">
                      <a :href="`mailto:${e.email}`">{{ e.email }}</a>
                      <MusubiStatusPill v-if="e.email_status === 'found'" status="found" tone="success" label="found" class="ms-1" title="Published on the company's own website" />
                      <MusubiStatusPill v-else-if="e.email_status === 'verified'" status="verified" tone="success" label="verified" class="ms-1" title="Mailbox verified (SMTP)" />
                      <MusubiStatusPill v-else-if="e.email_status === 'inferred'" status="inferred" tone="warning" :label="'inferred' + (e.email_confidence != null ? ` ${e.email_confidence}%` : '')" class="ms-1" :title="`Inferred from the firm's email pattern (confidence ${e.email_confidence ?? '?'}%)`" />
                    </span>
                    <span v-if="e.direct_dial"><a :href="`tel:${e.direct_dial}`" class="ms-1">{{ e.direct_dial }}</a></span>
                  </div>
                </div>
              </div>
            </dd></div>
            <div><dt>Sector (SIC)</dt><dd>
              <span v-if="lead.sectors && lead.sectors.length">
                <MusubiStatusPill v-for="s in lead.sectors" :key="s.code" :status="s.code" tone="neutral" :label="s.label" class="me-1 mb-1" :title="`SIC ${s.code}`" />
              </span>
              <span v-else class="text-muted">—</span>
            </dd></div>
          </dl>
        </MusubiSectionCard>

        <MusubiSectionCard title="Provenance" icon="shield">
          <dl class="lead-dl">
            <div><dt>Source</dt><dd>{{ lead.platform }} <span class="text-muted small" v-if="lead.source_type">/ {{ lead.source_type }}</span> <MusubiStatusPill v-if="lead.discovery_source" status="discovery_source" tone="neutral" :label="lead.discovery_source" /></dd></div>
            <!-- Agency-crawl provenance: an unverified referring-agency hint from
                 the portfolio discovery. Shown only until enrichment confirms an
                 agency (current_agency) — then the verified value stands alone. -->
            <div v-if="lead.discovery_referred_by && !lead.current_agency"><dt>Referred by</dt><dd>
              <span class="text-muted">Found via {{ lead.discovery_referred_by }}'s portfolio - unverified until enrichment confirms</span>
            </dd></div>
            <div><dt>Temperature</dt><dd>{{ lead.temperature }}</dd></div>
            <div v-if="lead.business_status"><dt>Site status</dt><dd>
              <span v-if="lead.business_status === 'active'" class="text-success">Active</span>
              <span v-else-if="lead.business_status === 'blocked'" class="text-muted">Blocked <CygnusIcon name="shield" title="Our crawler was blocked (anti-bot), but the domain resolves — site is likely up. Check manually." /></span>
              <span v-else class="text-danger">{{ lead.business_status }} <CygnusIcon name="warning" title="Site looks dead/parked — likely no longer trading" /></span>
            </dd></div>
            <div v-if="lead.ssl_status"><dt>SSL / security</dt><dd>
              <span v-if="lead.ssl_status === 'valid'" class="text-success">Valid <CygnusIcon name="lock" /></span>
              <span v-else-if="lead.ssl_status === 'expired'" class="text-danger fw-semibold">Certificate expired <CygnusIcon name="warning" title="Browsers show a security warning to every visitor — strong outreach hook" /></span>
              <span v-else-if="lead.ssl_status === 'invalid'" class="text-danger fw-semibold">Certificate invalid <CygnusIcon name="warning" title="Cert mismatch/untrusted — browsers warn visitors. Outreach hook" /></span>
              <span v-else-if="lead.ssl_status === 'none'" class="text-danger fw-semibold">No HTTPS <CygnusIcon name="warning" title="Site has no working TLS — shown as Not Secure. Outreach hook" /></span>
            </dd></div>
            <div><dt>Headline</dt><dd>{{ lead.headline || '—' }}</dd></div>
            <div><dt>Dedup key</dt><dd class="font-monospace small">{{ lead.dedup_key }}</dd></div>
            <div><dt>Discovered</dt><dd>{{ fmtDate(lead.created) }}</dd></div>
            <div><dt>Enriched</dt><dd>{{ lead.enriched_at ? fmtDate(lead.enriched_at) : '—' }}</dd></div>
          </dl>
          <div v-if="lead.signals && lead.signals.length" class="lead-signals">
            <div class="lead-dl-h">Matched signals</div>
            <span v-for="s in lead.signals" :key="s.id" class="lead-sig">{{ s.name }} (+{{ s.weight }})</span>
          </div>
        </MusubiSectionCard>
      </div>

      <MusubiSectionCard title="Enrichment methods" icon="layer-group" class="lead-enrich">
        <template #actions>
          <span class="text-muted small">mode = global · Run = this lead</span>
        </template>
        <table v-if="methods.length" class="enrich-table">
          <thead><tr><th>Mode</th><th>Method</th><th>Status</th><th>Last result</th><th></th></tr></thead>
          <tbody>
            <tr v-for="m in methods" :key="m.name">
              <td>
                <select class="enrich-mode form-select form-select-sm" :value="m.mode" @change="setMode(m, $event.target.value)" title="off = on-demand only · gap-fill = AI fills only if empty">
                  <option value="off">Off</option>
                  <option value="on">On</option>
                  <option v-if="m.supports_fallback" value="fallback">Gap-fill</option>
                </select>
              </td>
              <td>{{ m.label }}</td>
              <td>
                <span v-if="m.outcome" :class="outcomeClass(m.outcome)">{{ m.outcome }}</span>
                <span v-else class="text-muted small">not run</span>
                <span v-if="m.runs > 1" class="text-muted small"> ×{{ m.runs }}</span>
              </td>
              <td class="text-muted small">{{ m.detail || '—' }}</td>
              <td class="enrich-acts">
                <button class="btn btn-sm btn-link p-0" :disabled="busy" @click="runMethod(m)">
                  <CygnusIcon name="spinner" :spin="true" v-if="runningMethod === m.name" />
                  <span v-else>{{ m.outcome ? 'Re-run' : 'Run' }}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small">Loading methods…</p>
      </MusubiSectionCard>

      <MusubiSectionCard v-if="lead.marketing_summary || lead.outreach_opener" title="Outreach angle" icon="note-sticky" class="lead-outreach">
        <div v-if="lead.marketing_summary" class="lead-msum">
          <div class="lead-dl-h">Current marketing / news</div>
          <p>{{ lead.marketing_summary }}</p>
        </div>
        <div v-if="lead.outreach_opener">
          <div class="lead-dl-h">Suggested opener
            <button class="btn btn-sm btn-link p-0 ms-2" @click="copyOpener">{{ copied ? 'Copied!' : 'Copy' }}</button>
          </div>
          <blockquote class="lead-opener">{{ lead.outreach_opener }}</blockquote>
        </div>
      </MusubiSectionCard>

      <MusubiSectionCard v-if="lead.social_signal_score || (lead.social_signals && lead.social_signals.length)" title="Growth &amp; buying signals" icon="bullhorn" class="lead-bsig">
        <template #actions><span class="bsig-score">{{ lead.social_signal_score }}/100</span></template>
        <ul v-if="lead.social_signals && lead.social_signals.length" class="bsig-list">
          <li v-for="(s, i) in lead.social_signals" :key="i">
            <strong>{{ s.signal }}</strong> <span class="text-muted small">— {{ s.evidence }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small">No specific signals detected.</p>
      </MusubiSectionCard>

      <!-- Agency clients — the reverse of the "Current agency" link above. Only
           renders on a record that other prospects are connected to. -->
      <MusubiSectionCard v-if="agencyClients.length" title="Agency clients" icon="palette" class="lead-agcl">
        <template #actions><span class="agcl-n">{{ agencyClients.length }}</span></template>
        <p class="text-muted small mb-2">
          Prospects you hold that are connected to this agency - either their own site credits it,
          or it lists them as a client.
        </p>
        <div class="agcl">
          <div v-for="c in agencyClients" :key="c.id" class="agcl-row">
            <a class="agcl-name" :href="`/acquire/prospects/${c.id}`">{{ c.label }}</a>
            <span class="agcl-meta text-muted small">
              <span v-if="c.stage">{{ c.stage }}</span>
              <span v-if="c.score"> · score {{ c.score }}</span>
              <span v-if="c.website"> · {{ shortHost(c.website) }}</span>
              <span class="agcl-via" :title="c.via === 'footer_credit' ? 'Confirmed from the client\'s own website' : 'Listed on this agency\'s portfolio page - not yet confirmed from the client site'">
                · {{ c.via === 'footer_credit' ? 'credits this agency' : 'listed by agency' }}
              </span>
            </span>
          </div>
        </div>
      </MusubiSectionCard>

      <!-- Shared directors — other registered companies carrying the SAME
           Companies House officer identity as this one. -->
      <MusubiSectionCard v-if="connections.length" title="Shared directors" icon="diagram" class="lead-conn">
        <template #actions><span class="conn-n">{{ connections.length }}</span></template>
        <p class="text-muted small mb-2">
          Companies that share a director with this one, matched on the Companies House
          officer record rather than the name. A shared director is a warm route in.
        </p>
        <div class="conn">
          <div v-for="c in connections" :key="c.company_id" class="conn-row">
            <a v-if="c.prospect_id" class="conn-name" :href="`/acquire/prospects/${c.prospect_id}`">{{ c.label }}</a>
            <span v-else class="conn-name conn-name--plain">{{ c.label }}</span>
            <span class="conn-meta text-muted small">
              shares:
              <span v-for="(o, i) in c.officers" :key="i">{{ i ? ', ' : '' }}{{ o.name }} <span class="conn-role">({{ o.role }})</span></span>
              <span v-if="!c.prospect_id" class="conn-untracked"> · not in your prospects</span>
            </span>
          </div>
        </div>
      </MusubiSectionCard>

      <MusubiSectionCard v-if="people.length" title="People" icon="user-check" class="lead-people">
        <p class="text-muted small mb-2">
          People who told us they work here — they entered a work email on one of your
          contact forms. Self-identified contacts are your highest-quality named contacts,
          so we prefer them when choosing who to reach.
        </p>
        <div class="ppl">
          <div v-for="p in people" :key="p.id" class="ppl-row">
            <div class="ppl-icon"><CygnusIcon name="user" /></div>
            <div class="ppl-body">
              <div class="ppl-head">
                <strong>{{ p.name || p.email }}</strong>
                <MusubiStatusPill status="self_identified" tone="success" label="Self-identified" class="ms-1" title="Entered their own work email on your contact form" />
              </div>
              <div class="ppl-meta text-muted small">
                {{ p.email }}
                <span v-if="p.source_ref"> · from your contact form ({{ p.source_ref }})</span>
                <span v-if="p.last_seen"> · last seen {{ fmtDate(p.last_seen) }}</span>
              </div>
            </div>
          </div>
        </div>
      </MusubiSectionCard>

      <!-- Task #51 — business-updates stream for this prospect's company:
           CH filings, tech/website changes, identified visits, deliverability. -->
      <MusubiSectionCard title="Activity" icon="stream" class="lead-activity">
        <p class="text-muted small mb-2">
          Business updates on this company - newest first. Events appear as Yorlin watches the company.
        </p>
        <div v-if="eventCategoryChips.length" class="act-chips">
          <button type="button" class="act-chip" :class="{ 'act-chip--on': !eventFilter }" @click="setEventFilter(null)">
            All <span class="act-chip__n">{{ eventsTotal }}</span>
          </button>
          <button v-for="c in eventCategoryChips" :key="c.key" type="button" class="act-chip" :class="{ 'act-chip--on': eventFilter === c.key }" @click="setEventFilter(c.key)">
            <CygnusIcon :name="c.icon" class="me-1" />{{ c.label }} <span class="act-chip__n">{{ c.count }}</span>
          </button>
        </div>
        <div v-if="eventsLoading" class="text-muted small py-2">Loading activity…</div>
        <div v-else-if="events.length" class="act-tl">
          <div v-for="e in events" :key="e.id" class="act-row">
            <div class="act-icon"><CygnusIcon :name="e.icon || 'stream'" /></div>
            <div class="act-body">
              <div class="act-head">
                <strong>{{ e.title }}</strong>
                <span class="act-when text-muted" :title="fmtDate(e.occurred_at)">{{ relTime(e.occurred_at) }}</span>
              </div>
              <div v-if="e.payload && Object.keys(e.payload).length" class="act-detail">
                <button type="button" class="btn btn-link btn-sm p-0 act-toggle" @click="toggleEventDetail(e.id)">
                  {{ expandedEvents.includes(e.id) ? 'Hide details' : 'Details' }}
                </button>
                <pre v-if="expandedEvents.includes(e.id)" class="act-payload">{{ JSON.stringify(e.payload, null, 2) }}</pre>
              </div>
            </div>
          </div>
          <div v-if="eventsLastPage > 1" class="act-more">
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="eventsPage >= eventsLastPage" @click="loadMoreEvents">
              Load more
            </button>
            <span class="text-muted small ms-2">page {{ eventsPage }} / {{ eventsLastPage }}</span>
          </div>
        </div>
        <p v-else class="text-muted small">No business updates yet - events appear as Yorlin watches this company.</p>
      </MusubiSectionCard>

      <MusubiSectionCard title="Engagement timeline" icon="envelope-open-text" class="lead-engage">
        <p class="text-muted small mb-2">
          Every message we've sent this prospect and how they responded — newest first.
          <span v-if="engageContact"> Contact on file: <strong>{{ engageContact }}</strong>.</span>
        </p>
        <div v-if="engagement.length" class="etl">
          <div v-for="(e, i) in engagement" :key="i" class="etl-row" :class="{ 'etl-row--reply': e.replied_at }">
            <div class="etl-icon"><CygnusIcon v-bind="engageIcon(e)" /></div>
            <div class="etl-body">
              <div class="etl-head">
                <strong>{{ e.source === 'drip' ? (e.flow_name || 'Drip flow') : (e.campaign_name || 'Campaign') }}</strong>
                <span v-if="e.step" class="text-muted small"> · {{ e.step }}</span>
                <MusubiStatusPill :status="e.status" class="ms-1" />
              </div>
              <div v-if="e.subject" class="etl-subject text-muted small">“{{ e.subject }}”</div>
              <div class="etl-chain">
                <span v-for="s in engageChain(e)" :key="s.key" class="etl-step" :class="{ 'etl-step--on': s.at, 'etl-step--reply': s.key === 'replied' && s.at }" :title="s.at ? fmtDate(s.at) : ''">
                  <CygnusIcon :name="s.icon" /> {{ s.label }}<span v-if="s.at" class="text-muted"> · {{ fmtDate(s.at) }}</span>
                </span>
              </div>
              <div v-if="e.replied_at" class="etl-replied">
                <CygnusIcon name="arrow-left" /> Replied — {{ e.source === 'drip' ? (e.flow_name || 'flow') : (e.campaign_name || 'campaign') }}<span v-if="e.step">, {{ e.step }}</span> · via {{ e.email }}
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small">No messages sent to this prospect yet.</p>
      </MusubiSectionCard>

      <MusubiSectionCard title="CRM hand-off" icon="shuffle" class="lead-crm">
        <template #actions>
          <button class="btn btn-outline-success btn-sm" :disabled="crmBusy" @click="sendToCrm">
            <CygnusIcon :name="crmBusy ? 'spinner' : 'envelope'" :spin="crmBusy" class="me-1" />Send to CRM
          </button>
        </template>
        <p class="text-muted small mb-2">
          Where this prospect stands in each connected CRM. Promoting a lead also pushes it automatically.
        </p>
        <div v-if="crmStatus.length" class="crm-rows">
          <div v-for="c in crmStatus" :key="c.key" class="crm-row">
            <span class="crm-row__name">{{ c.label }}</span>
            <template v-if="!c.configured">
              <span class="text-muted small">Not connected — <a href="/admin/crm-connections">connect {{ c.label }}</a></span>
            </template>
            <template v-else-if="c.pushed">
              <span class="text-success small"><CygnusIcon name="check-circle" class="me-1" />Pushed<span v-if="c.pushed_at"> · {{ fmtDate(c.pushed_at) }}</span></span>
              <a v-if="c.external_url" :href="c.external_url" target="_blank" rel="noopener" class="small ms-2">open in {{ c.label }} ↗</a>
            </template>
            <template v-else-if="c.last_error">
              <span class="text-danger small"><CygnusIcon name="warning" class="me-1" />Failed — {{ c.last_error }}</span>
            </template>
            <template v-else>
              <span class="text-muted small">Not pushed yet</span>
            </template>
          </div>
        </div>
        <p v-else class="text-muted small">No CRM connected for this workspace. <a href="/admin/crm-connections">Connect one</a> to hand off promoted leads.</p>
      </MusubiSectionCard>

      <MusubiSectionCard title="Change history" icon="refresh" class="lead-audit">
        <p class="text-muted small">Audited enrichment changes (newest first) — what changed, when, and what triggered it.</p>
        <table v-if="audit.length" class="es-table">
          <thead><tr><th>When</th><th>Trigger</th><th>Changes</th></tr></thead>
          <tbody>
            <tr v-for="(a, i) in audit" :key="i">
              <td class="text-muted small">{{ fmtDate(a.at) }}</td>
              <td><MusubiStatusPill status="trigger" tone="neutral" :label="a.trigger" /></td>
              <td class="small">
                <div v-for="(ft, f) in a.changes" :key="f">
                  <strong>{{ f }}</strong>: <span class="text-muted">{{ ft.from === null ? '—' : ft.from }}</span> → {{ ft.to === null ? '—' : ft.to }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small">No recorded changes yet.</p>
      </MusubiSectionCard>

      <MusubiSectionCard title="Captured text" icon="list" class="lead-raw">
        <pre v-if="lead.raw_text">{{ lead.raw_text }}</pre>
        <p v-else class="text-muted small">No captured text.</p>
      </MusubiSectionCard>
    </template>

    <!-- Per-contact compose (follow-up-pool slice A) — opened from "Send email"
         and from a tech-alert deep-link (?compose=1). -->
    <MusubiComposeModal :open="composeOpen" :context="composeContext" @close="composeOpen = false" @sent="onComposed" />
  </div>
</template>

<script>
/**
 * AdminLeadDetail — full view of one discovered lead: firmographics (all §4
 * enrichment fields), provenance + matched signals, captured text, and triage
 * actions. Reads the lead id from the route (/acquire/leads/{id}).
 */
import useApi from "@apexl/icarus-core/src/services/api";
import { apiErrorMessage } from "./util/aiError";

export default {
  name: 'AdminLeadDetail',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return { api: useApi(), lead: null, methods: [], audit: [], engagement: [], people: [], agencyClients: [], connections: [], crmStatus: [], crmBusy: false, canOverride: false, manualAiAllowed: false, canEditProspect: false, busyAi: false, loading: false, busy: false, runningMethod: null, copied: false, error: null, message: '', editing: false, savingEdit: false, edit: {}, composeOpen: false, composeContext: {}, autoComposeChecked: false,
      // Task #51 — prospect activity (business-updates) stream.
      events: [], eventCategories: {}, eventFilter: null, eventsLoading: false, eventsPage: 1, eventsLastPage: 1, eventsTotal: 0, expandedEvents: [] };
  },
  computed: {
    leadId() {
      // The page is server-mounted at /acquire/leads/{id}; the SPA router has
      // no :id param, so $route.params is empty. Read the id from the path
      // (fall back to the router param if a client route ever defines it).
      const fromRoute = Number(this.$route?.params?.id || 0);
      if (fromRoute) return fromRoute;
      const m = (typeof window !== 'undefined' ? window.location.pathname : '').match(/\/acquire\/(?:prospects|leads)\/(\d+)/);
      return m ? Number(m[1]) : 0;
    },
    terminal() { return this.lead && (this.lead.stage === 'rejected' || this.lead.stage === 'promoted'); },
    // The person behind the sends — the named contact the subscriber was
    // enrolled as (recipient intelligence), else the address used.
    engageContact() {
      const e = this.engagement[0];
      if (!e) return '';
      const badge = e.contact_status && e.contact_status !== 'generic' ? ` (${e.contact_status})` : '';
      return e.name ? `${e.name} <${e.email}>${badge}` : e.email;
    },
    hasSocial() { return this.lead && this.lead.social && Object.keys(this.lead.social).length > 0; },
    // Email-verification badge (MX + SMTP state stored on the company email).
    // verified → success, catch_all → neutral, risky → warning, dead → danger.
    emailVerify() {
      const s = this.lead?.email_verified_state;
      if (!s) return null;
      const at = this.lead?.email_verified_at;
      const when = at ? ` ${this.relTime(at)}` : '';
      switch (s) {
        case 'verified':  return { status: 'verified', tone: 'success', label: `Verified${when}`, title: at ? `Mailbox verified (MX + SMTP) ${this.fmtDate(at)}` : 'Mailbox verified (MX + SMTP)' };
        case 'catch_all': return { status: 'catch_all', tone: 'neutral', label: 'Accepts all mail', title: 'The domain accepts every recipient, so this specific mailbox cannot be confirmed. Sends are still allowed.' };
        case 'risky':     return { status: 'risky', tone: 'warning', label: 'Risky', title: 'MX present but the mailbox could not be confirmed (SMTP unreachable, greylisted, or timed out). Sends are still allowed.' };
        case 'dead':      return { status: 'dead', tone: 'danger', label: 'Address dead - sends blocked', title: 'No mailbox, or the domain cannot receive mail. Sends to this address are blocked.' };
        default:          return null;
      }
    },
    // TPS/CTPS call-screening badge (Task #55). Advisory guidance beside the
    // phone: callable → OK to call, tps/ctps → do not cold call, invalid → neutral
    // warning. Null (no phone / not screened) → no badge.
    tpsVerdict() {
      const v = this.lead?.tps_screening;
      if (!v || !v.result) return null;
      const when = v.screened_at ? ` · screened ${this.relTime(v.screened_at)}` : '';
      switch (v.result) {
        case 'callable':
          return { status: 'callable', tone: 'success', label: `OK to call${when}`, title: v.screened_at ? `Screened against TPS + CTPS ${this.fmtDate(v.screened_at)} — not on either register. Advisory only.` : 'Not on the TPS or CTPS register. Advisory only.' };
        case 'tps':
          return { status: 'tps', tone: 'danger', label: 'TPS registered - do not cold call', title: 'This number is on the Telephone Preference Service register (individuals / sole traders). Making an unsolicited marketing call is unlawful under PECR reg. 21.' };
        case 'ctps':
          return { status: 'ctps', tone: 'danger', label: 'CTPS registered - do not cold call', title: 'This number is on the Corporate Telephone Preference Service register. Making an unsolicited marketing call is unlawful under PECR reg. 21.' };
        case 'unscreened_invalid':
          return { status: 'unscreened', tone: 'neutral', label: 'Not screened - no valid number', title: 'No phone number here could be normalised to a UK format, so it could not be screened against TPS/CTPS. Verify the number before calling.' };
        default:
          return null;
      }
    },
    confidenceTone() {
      const c = this.lead?.match_confidence ?? 0;
      return c >= 75 ? 'success' : (c >= 50 ? 'warning' : 'danger');
    },
    budgetTone() {
      return {
        minimal: 'neutral', modest: 'info',
        moderate: 'accent', substantial: 'success',
      }[this.lead?.budget_band] || 'neutral';
    },
    // Category filter chips for the Activity stream — only categories that have
    // at least one event, each with its label + icon + count.
    eventCategoryChips() {
      return Object.entries(this.eventCategories || {})
        .filter(([, c]) => c && c.count > 0)
        .map(([key, c]) => ({ key, label: c.label, icon: c.icon, count: c.count }));
    },
    financialsLabel() {
      const f = this.lead?.financials || {};
      const gbp = (n) => '£' + Math.round(n).toLocaleString();
      const parts = [];
      if (f.turnover) parts.push('turnover ' + gbp(f.turnover));
      if (f.net_assets != null) parts.push('net assets ' + gbp(f.net_assets));
      else if (f.cash) parts.push('cash ' + gbp(f.cash));
      return parts.length ? '· ' + parts.join(' · ') : '';
    },
  },
  mounted() {
    // Load the lead + engagement first so a tech-alert deep-link (?compose=1)
    // can prefill the contact address off the loaded prospect data.
    Promise.all([this.load(), this.loadEngagement()]).then(() => this.maybeAutoCompose());
    this.loadEnrichment();
    this.loadCrmStatus();
    this.loadPeople();
    this.loadAgencyClients();
    this.loadConnections();
    this.loadEvents();
    this.checkEditPermission();
  },
  methods: {
    /**
     * Manual prospect edits write to the SHARED company corpus, so the PATCH
     * /admin/leads/{id} route is gated Ops::PLATFORM. Mirror that on the FE:
     * only platform operators (plan.can_manage on /me/usage — same signal the
     * Data views / Prospects-browser surfaces use) see the "Edit details"
     * affordance. Fail-CLOSED: a usage-endpoint blip hides the button rather
     * than showing an edit that the API would 403 anyway.
     */
    async checkEditPermission() {
      try {
        const res = await this.api.get('/api/v1/me/usage');
        this.canEditProspect = !!res?.plan?.can_manage;
      } catch (e) {
        this.canEditProspect = false;
      }
    },
    // ── per-contact compose ──
    // The contact the compose targets: the primary (or first) decision-maker with
    // an email, else the lead's general email.
    composeContact() {
      const l = this.lead || {};
      const people = [...(l.contacts || []), ...(l.employees || [])];
      const primary = people.find((c) => c.primary && c.email) || people.find((c) => c.email);
      if (primary) return { email: primary.email, name: primary.name || '' };
      return { email: l.email || '', name: l.name || l.company || '' };
    },
    openCompose(overrides = {}) {
      const c = this.composeContact();
      const subId = this.engagement.find((e) => e.subscriber_id)?.subscriber_id || 0;
      this.composeContext = {
        to_email: overrides.to_email || c.email,
        to_name: overrides.to_name != null ? overrides.to_name : c.name,
        subject: overrides.subject || '',
        prospect_id: this.leadId,
        subscriber_id: subId,
      };
      this.composeOpen = true;
    },
    async onComposed() {
      // Refresh the timeline so the just-queued send shows immediately.
      await this.loadEngagement();
    },
    // Tech-alert "Start conversation" deep-links here with ?compose=1 (+ optional
    // subject). Open the compose panel once, prefilled with a plain subject.
    maybeAutoCompose() {
      if (this.autoComposeChecked || !this.lead) return;
      this.autoComposeChecked = true;
      const q = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      const wants = q.get('compose') === '1' || q.get('from') === 'tech-alert';
      if (!wants || this.lead.unsubscribed) return;
      this.openCompose({ subject: q.get('subject') || 'Your website' });
    },
    async loadCrmStatus() {
      if (!this.leadId) return;
      try {
        const res = await this.api.get(`/api/v1/admin/prospects/${this.leadId}/crm-status`);
        this.crmStatus = res?.data?.connectors ?? res?.connectors ?? [];
      } catch (e) { /* non-fatal — CRM status is additive */ }
    },
    async sendToCrm() {
      this.crmBusy = true; this.error = null; this.message = '';
      try {
        const res = await this.api.post(`/api/v1/admin/prospects/${this.leadId}/crm-push`, {});
        const p = res?.data ?? res;
        this.message = p.message || 'Queued push to CRM.';
        await this.loadCrmStatus();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'CRM push failed.'; }
      this.crmBusy = false;
    },
    async loadEngagement() {
      if (!this.leadId) return;
      try {
        const res = await this.api.get(`/api/v1/admin/prospects/${this.leadId}/engagement`);
        this.engagement = res?.data?.data ?? res?.data ?? [];
      } catch (e) { /* non-fatal — engagement is additive to the page */ }
    },
    async loadPeople() {
      if (!this.leadId) return;
      try {
        const res = await this.api.get(`/api/v1/admin/leads/${this.leadId}/people`);
        this.people = res?.data?.data ?? res?.data ?? [];
      } catch (e) { /* non-fatal — the People card is additive to the page */ }
    },
    // The prospects connected to THIS record as their agency. Empty on a normal
    // (non-agency) prospect, which hides the card.
    async loadAgencyClients() {
      if (!this.leadId) return;
      try {
        const res = await this.api.get(`/api/v1/admin/leads/${this.leadId}/agency-clients`);
        this.agencyClients = res?.data?.data ?? res?.data ?? [];
      } catch (e) { /* non-fatal — the Agency clients card is additive to the page */ }
    },
    // Companies sharing a Companies House officer with this prospect's company.
    async loadConnections() {
      if (!this.leadId) return;
      try {
        const res = await this.api.get(`/api/v1/admin/leads/${this.leadId}/connections`);
        this.connections = res?.data?.data ?? res?.data ?? [];
      } catch (e) { /* non-fatal — the Shared directors card is additive to the page */ }
    },
    /** Bare hostname for a compact list cell (falls back to the raw string). */
    shortHost(url) {
      try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return url; }
    },
    // ── Activity (prospect events) stream ──
    async loadEvents(page = 1, append = false) {
      if (!this.leadId) return;
      this.eventsLoading = !append;
      try {
        const qs = new URLSearchParams({ page: String(page), per_page: '25' });
        if (this.eventFilter) qs.set('category', this.eventFilter);
        const res = await this.api.get(`/api/v1/admin/leads/${this.leadId}/events?${qs.toString()}`);
        const body = res?.data ?? res ?? {};
        const rows = body.data || [];
        this.events = append ? [...this.events, ...rows] : rows;
        this.eventCategories = body.categories || {};
        this.eventsPage = body.page || 1;
        this.eventsLastPage = body.last_page || 1;
        // `total` is the filtered count; the "All" chip wants the full company
        // total = sum of every category's count.
        this.eventsTotal = Object.values(this.eventCategories).reduce((sum, c) => sum + (c?.count || 0), 0);
      } catch (e) { /* non-fatal — Activity is additive to the page */ }
      this.eventsLoading = false;
    },
    setEventFilter(cat) {
      if (this.eventFilter === cat) return;
      this.eventFilter = cat;
      this.expandedEvents = [];
      this.loadEvents(1, false);
    },
    loadMoreEvents() {
      if (this.eventsPage < this.eventsLastPage) this.loadEvents(this.eventsPage + 1, true);
    },
    toggleEventDetail(id) {
      const i = this.expandedEvents.indexOf(id);
      if (i === -1) this.expandedEvents.push(id);
      else this.expandedEvents.splice(i, 1);
    },
    engageChain(e) {
      return [
        { key: 'sent',      label: 'Sent',      icon: 'envelope',      at: e.sent_at },
        { key: 'delivered', label: 'Delivered', icon: 'envelope',      at: e.delivered_at },
        { key: 'opened',    label: 'Opened',    icon: 'envelope',      at: e.opened_at },
        { key: 'clicked',   label: 'Clicked',   icon: 'hand',          at: e.clicked_at },
        { key: 'replied',   label: 'Replied',   icon: 'arrow-left',    at: e.replied_at },
        ...((e.bounced_at || e.complained_at) ? [{ key: 'bounced', label: 'Bounced', icon: 'warning', at: e.bounced_at || e.complained_at }] : []),
      ];
    },
    engageIcon(e) {
      if (e.replied_at) return { name: 'arrow-left', class: 'etl-ic--reply' };
      if (e.bounced_at || e.complained_at) return { name: 'warning', class: 'etl-ic--warn' };
      if (e.clicked_at) return { name: 'hand', class: 'etl-ic--engage' };
      if (e.opened_at) return { name: 'envelope', class: 'etl-ic--engage' };
      return { name: 'envelope', class: 'etl-ic--sent' };
    },
    openEdit() {
      const l = this.lead || {};
      this.edit = {
        website: l.website || '', company: l.company || '', company_number: l.company_number || '',
        address: l.address || '', region: l.region || '', email: l.email || '',
        phone: l.phone || '', current_agency: l.current_agency || '',
      };
      this.editing = true;
      this.error = null; this.message = '';
    },
    async saveEdit() {
      this.savingEdit = true; this.error = null; this.message = '';
      try {
        const res = await this.api.patch(`/api/v1/admin/leads/${this.leadId}`, this.edit);
        const p = res?.data ?? res;
        if (p.lead) this.lead = p.lead;
        this.editing = false;
        this.message = `Saved (${(p.updated || []).join(', ')}) — re-enriching in the background.`;
        this.loadEnrichment();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Save failed.'; }
      this.savingEdit = false;
    },
    async load() {
      if (!this.leadId) { this.error = 'No lead id.'; return; }
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get(`/api/v1/admin/leads/${this.leadId}`);
        this.lead = res?.data ?? res;
      } catch (e) {
        this.error = e?.response?.status === 404 ? 'Lead not found.' : (e?.message || 'Failed to load lead.');
      }
      this.loading = false;
    },
    async loadEnrichment() {
      if (!this.leadId) return;
      try {
        const res = await this.api.get(`/api/v1/admin/leads/${this.leadId}/enrichment`);
        // useApi resolves to the response BODY ({data, can_override, …}); fall
        // back to res.data in case a raw axios response is ever returned.
        const body = (res && res.can_override !== undefined) ? res : (res?.data ?? res ?? {});
        this.methods = body.data || [];
        this.canOverride = !!body.can_override;
        this.manualAiAllowed = !!body.manual_ai_allowed;
      } catch (e) { /* non-fatal */ }
      this.loadAudit();
    },
    async loadAudit() {
      if (!this.leadId) return;
      try {
        const res = await this.api.get(`/api/v1/admin/leads/${this.leadId}/audit`);
        // useApi resolves to the BODY ({data: [...]}); res.data IS the array.
        // (old `(res?.data ?? res)?.data` read body.data.data → always empty.)
        this.audit = res?.data?.data ?? res?.data ?? [];
      } catch (e) { /* non-fatal */ }
    },
    async aiEnrich() {
      this.busyAi = true; this.error = null; this.message = '';
      try {
        const res = await this.api.post(`/api/v1/admin/leads/${this.leadId}/enrich-ai`, {});
        const p = res?.data ?? res;
        const found = Object.values(p.methods || {}).filter((o) => o === 'found');
        this.message = found.length ? `AI pass complete — ${found.length} method(s) returned data.` : 'AI pass complete — nothing new found.';
        await this.load();
        await this.loadEnrichment();
      } catch (e) { this.error = apiErrorMessage(e, 'AI enrich failed.'); }
      this.busyAi = false;
    },
    async runMethod(m) {
      let override = false;
      // Only successful methods need override to re-run; not_found/failed retry freely.
      if (m.outcome === 'found') {
        if (!this.canOverride) { this.error = 'This method already found data; you lack permission to re-run it.'; return; }
        if (!window.confirm(`Re-run "${m.label}" for this lead? (override)`)) return;
        override = true;
      }
      this.busy = true; this.runningMethod = m.name; this.error = null; this.message = '';
      try {
        const res = await this.api.post(`/api/v1/admin/leads/${this.leadId}/enrich/${m.name}`, { override });
        const p = res?.data ?? res;
        const outcome = (p.methods || {})[m.name];
        this.message = outcome === 'found' ? `${m.label}: found data.` : `${m.label}: nothing found.`;
        await this.load();
        await this.loadEnrichment();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Run failed.'; }
      this.busy = false; this.runningMethod = null;
    },
    async setMode(m, mode) {
      try {
        await this.api.post(`/api/v1/admin/leads/enrich-methods/${m.name}`, { mode });
        await this.loadEnrichment();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Mode change failed.'; }
    },
    outcomeClass(o) { return o === 'found' ? 'text-success' : (o === 'failed' ? 'text-danger' : 'text-muted'); },
    copyOpener() {
      const t = this.lead?.outreach_opener || '';
      if (!t) return;
      try { navigator.clipboard.writeText(t); this.copied = true; setTimeout(() => { this.copied = false; }, 1500); } catch (_) { /* clipboard blocked */ }
    },
    async enrich() {
      this.busy = true; this.error = null; this.message = '';
      try {
        const res = await this.api.post(`/api/v1/admin/leads/${this.leadId}/enrich`, {});
        const p = res?.data ?? res;
        const found = Object.values(p.methods || {}).filter((o) => o === 'found');
        this.message = found.length
          ? `Enrichment complete — ${found.length} method(s) returned data.`
          : 'Enrichment complete — nothing new found.';
        await this.load();
        await this.loadEnrichment();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Enrich failed.'; }
      this.busy = false;
    },
    async promote() {
      if (!window.confirm('Promote this lead to the CRM? (marks it promoted)')) return;
      await this.act('promote', 'Promoted → CRM');
    },
    async act(action, label) {
      this.busy = true; this.error = null; this.message = '';
      try {
        await this.api.post(`/api/v1/admin/leads/${this.leadId}/${action}`, {});
        this.message = `${label}.`;
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Action failed.'; }
      this.busy = false;
    },
    fmtDate(ts) { return ts ? new Date(ts * 1000).toLocaleString() : '—'; },
    // Compact relative time for a unix (seconds) timestamp — "3d ago", "2mo ago".
    relTime(ts) {
      if (!ts) return '';
      const secs = Math.floor(Date.now() / 1000) - ts;
      if (secs < 60) return 'just now';
      const mins = Math.floor(secs / 60);
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days < 30) return `${days}d ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months}mo ago`;
      return `${Math.floor(months / 12)}y ago`;
    },
  },
};
</script>

<style scoped>
.musubi-page--lead { padding: var(--m-space-6, 32px); }
.lead-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.lead-back { font-size: 0.8rem; color: var(--m-text-secondary); text-decoration: none; }
.lead-back:hover { color: var(--m-accent); }
.lead-temp { font-size: 0.9rem; font-weight: 500; margin-left: 8px; }
.lead-kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; margin: 16px 0; }
.lead-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.lead-edit { margin-bottom: 16px; }
.lead-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
.lead-edit-grid label { display: flex; flex-direction: column; gap: 3px; font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--m-text-tertiary); }
.lead-edit-actions { margin-top: 12px; display: flex; align-items: center; gap: 8px; }
.lead-dl > div { display: flex; gap: 10px; padding: 5px 0; border-bottom: 1px solid var(--m-border); }
.lead-dl dt { width: 130px; flex-shrink: 0; color: var(--m-text-tertiary); font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.03em; }
.lead-dl dd { margin: 0; font-size: 0.88rem; word-break: break-word; }
.lead-dl-h { color: var(--m-text-tertiary); font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; margin: 12px 0 6px; }
.lead-sig { display: inline-block; background: var(--m-accent-soft); color: var(--m-accent); border-radius: 6px; padding: 2px 8px; font-size: 0.75rem; margin: 0 4px 4px 0; }
.lead-director { display: inline-block; background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 6px; padding: 1px 8px; margin: 0 4px 4px 0; font-size: 0.8rem; }
.lead-contacts { display: flex; flex-direction: column; gap: 6px; }
.lead-contact { background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 6px; padding: 4px 8px; }
.lead-contact__name { font-weight: 600; }
.lead-social a { display: inline-block; margin-right: 8px; text-transform: capitalize; color: var(--m-accent); text-decoration: none; }
.lead-social a:hover { text-decoration: underline; }
.lead-outreach { margin-top: 16px; }
.lead-msum p { margin: 0 0 10px; font-size: 0.9rem; }
.lead-opener { margin: 4px 0 0; padding: 10px 12px; border-left: 3px solid var(--m-accent); background: var(--m-bg-content); border-radius: 0 6px 6px 0; font-size: 0.92rem; font-style: italic; }
.lead-bsig { margin-top: 16px; }
.bsig-score { font-weight: 800; color: var(--m-accent); }
.bsig-list { margin: 0; padding-left: 18px; }
.bsig-list li { padding: 3px 0; font-size: 0.88rem; }
.lead-enrich { margin-top: 16px; }
.enrich-table { width: 100%; border-collapse: collapse; }
.enrich-table th { text-align: left; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 600; padding: 7px 6px; border-bottom: 1px solid var(--m-border); }
.enrich-table td { padding: 8px 6px; border-bottom: 1px solid var(--m-border); vertical-align: middle; font-size: 0.85rem; }
.enrich-acts { text-align: right; }
.enrich-toggle { background: none; border: none; cursor: pointer; font-size: 1.1rem; color: var(--m-text-tertiary); padding: 0 6px 0 0; vertical-align: middle; }
.enrich-toggle.on { color: var(--m-accent); }
.enrich-mode { width: auto; display: inline-block; font-size: 0.78rem; padding: 2px 6px; }
.lead-conn { margin-top: 16px; }
.conn-n { font-weight: 800; color: var(--m-accent); font-variant-numeric: tabular-nums; }
.conn { display: flex; flex-direction: column; }
.conn-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; padding: 6px 0; border-bottom: 1px solid var(--m-border); }
.conn-row:last-child { border-bottom: none; }
.conn-name { font-size: 0.88rem; font-weight: 600; color: var(--m-accent); text-decoration: none; }
.conn-name:hover { text-decoration: underline; }
.conn-name--plain { color: var(--m-text-primary); }
.conn-meta { margin-left: auto; }
.conn-role { opacity: 0.8; }
.conn-untracked { opacity: 0.8; }
.lead-agcl { margin-top: 16px; }
.agcl-n { font-weight: 800; color: var(--m-accent); font-variant-numeric: tabular-nums; }
.agcl { display: flex; flex-direction: column; }
.agcl-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; padding: 6px 0; border-bottom: 1px solid var(--m-border); }
.agcl-row:last-child { border-bottom: none; }
.agcl-name { font-size: 0.88rem; font-weight: 600; color: var(--m-accent); text-decoration: none; }
.agcl-name:hover { text-decoration: underline; }
.agcl-meta { margin-left: auto; }
.agcl-via { opacity: 0.85; }
.lead-activity { margin-top: 16px; }
.act-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.act-chip { display: inline-flex; align-items: center; border: 1px solid var(--m-border); background: var(--m-bg-content); color: var(--m-text-secondary); border-radius: 999px; padding: 3px 11px; font-size: 0.78rem; cursor: pointer; }
.act-chip:hover { border-color: var(--m-accent); color: var(--m-accent); }
.act-chip--on { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-accent); font-weight: 600; }
.act-chip__n { margin-left: 5px; font-variant-numeric: tabular-nums; opacity: 0.75; }
.act-tl { display: flex; flex-direction: column; gap: 8px; position: relative; }
.act-row { display: flex; gap: 12px; padding: 8px 0; }
.act-icon { width: 28px; height: 28px; border-radius: 8px; flex: none; display: flex; align-items: center; justify-content: center; background: var(--m-bg-card); border: 1px solid var(--m-border); color: var(--m-accent); }
.act-body { flex: 1; min-width: 0; }
.act-head { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; font-size: 0.88rem; }
.act-when { font-size: 0.76rem; flex: none; }
.act-detail { margin-top: 2px; }
.act-toggle { font-size: 0.74rem; }
.act-payload { margin: 6px 0 0; padding: 8px 10px; background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 6px; font-size: 0.72rem; color: var(--m-text-secondary); white-space: pre-wrap; max-height: 200px; overflow: auto; }
.act-more { margin-top: 10px; }
.lead-engage { margin-top: 16px; }
.etl { display: flex; flex-direction: column; gap: 10px; }
.etl-row { display: flex; gap: 12px; padding: 10px 12px; border: 1px solid var(--m-border); border-radius: 8px; background: var(--m-bg-content); }
.etl-row--reply { border-color: #7C3AED; background: color-mix(in srgb, #7C3AED 6%, var(--m-bg-content)); }
.etl-icon { width: 30px; height: 30px; border-radius: 8px; flex: none; display: flex; align-items: center; justify-content: center; background: var(--m-bg-card); border: 1px solid var(--m-border); }
.etl-ic--reply { color: #7C3AED; }
.etl-ic--engage { color: var(--m-accent); }
.etl-ic--warn { color: var(--m-status-warning); }
.etl-ic--sent { color: var(--m-text-tertiary); }
.etl-body { flex: 1; min-width: 0; }
.etl-head { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; font-size: 0.86rem; }
.etl-subject { margin: 2px 0; }
.etl-chain { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: 6px; }
.etl-step { font-size: 0.72rem; color: var(--m-text-tertiary); display: inline-flex; align-items: center; gap: 4px; }
.etl-step--on { color: var(--m-text-secondary); }
.etl-step--reply { color: #7C3AED; font-weight: 600; }
.etl-replied { margin-top: 8px; font-size: 0.8rem; font-weight: 600; color: #7C3AED; }
.lead-raw { margin-top: 16px; }
.lead-raw pre { white-space: pre-wrap; font-size: 0.8rem; color: var(--m-text-secondary); margin: 0; max-height: 320px; overflow: auto; }
.actions-bar { display: flex; gap: 8px; margin-bottom: 14px; }
.lead-crm { margin-top: 16px; }
.crm-rows { display: flex; flex-direction: column; gap: 6px; }
.crm-row { display: flex; align-items: baseline; gap: 10px; padding: 6px 10px; border: 1px solid var(--m-border); border-radius: 8px; background: var(--m-bg-content); }
.crm-row__name { font-weight: 600; min-width: 90px; font-size: 0.86rem; }
@media (max-width: 1024px) { .lead-kpis { grid-template-columns: repeat(2,1fr); } .lead-grid { grid-template-columns: 1fr; } .lead-edit-grid { grid-template-columns: 1fr; } }
@media (max-width: 640px)  { .lead-kpis { grid-template-columns: 1fr; } }
</style>
